import { create } from "zustand";
import { apiClient } from "../services/api";

export type UserRole = "ADMIN" | "DIRECTOR" | "SUPERVISOR" | "GUARD";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  hospital_id?: string | null;
  /**
   * The full set of hospitals this user is authorized to see. Populated from the
   * login response's user_hospitals assignments. Used to scope SUPERVISOR data
   * views to their assigned hospitals; null/empty means "no client-side filter"
   * (ADMIN/DIRECTOR see everything).
   */
  hospital_ids?: string[] | null;
  main_site_id?: string | null;
  status?: string | null;
  employeeId?: string | null;
}

interface AuthState {
  user: AuthUser | null;
  deviceId: string | null;
  token: string | null;
  loading: boolean;
  initialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  signup: (formData: any) => Promise<void>;
}

// ── Session token lifetime ──────────────────────────────────────────────────
// Every API call is authorised by the JWT from /api/guards/login (8h). The
// server used to accept a bare X-User-Email header when the token was missing
// or expired, so a stale browser session kept working; it no longer does.
// The dashboard therefore treats "no valid token" as signed out.
function tokenExpiryMs(token: string | null): number | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}
function tokenIsValid(token: string | null): boolean {
  const exp = tokenExpiryMs(token);
  return exp != null && exp > Date.now() + 30_000;
}
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSessionExpiry(token: string | null) {
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
  const exp = tokenExpiryMs(token);
  if (!exp) return;
  // setTimeout overflows past ~24.8 days; tokens are 8h so this is plenty.
  const delay = Math.max(0, Math.min(exp - Date.now(), 2 ** 31 - 1));
  expiryTimer = setTimeout(() => {
    void useAuthStore.getState().logout().then(() => {
      if (!window.location.pathname.startsWith("/login")) window.location.assign("/login");
    });
  }, delay);
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  deviceId: localStorage.getItem("deviceId"),
  token: localStorage.getItem("authToken"),
  loading: true,
  initialized: false,
  setUser(user) {
    set({ user });
  },
  async login(email, password) {
    set({ loading: true });

    // Demo mode - allow login without backend
    const demoUsers: Record<string, AuthUser & { password: string }> = {
      "admin@demo.com": {
        id: "demo-admin",
        email: "admin@demo.com",
        name: "Admin User",
        role: "ADMIN",
        password: "secureDemo123",
        employeeId: "EMP-ADMIN"
      },
      "admin@emory.local": {
        id: "emory-admin",
        email: "admin@emory.local",
        name: "Emory Admin",
        role: "ADMIN",
        password: "secureDemo123"
      },
      "admin@emory.edu": {
        id: "emory-admin-prod",
        email: "admin@emory.edu",
        name: "System Admin",
        role: "ADMIN",
        password: "secureDemo123"
      },
      "supervisor@demo.com": {
        id: "demo-supervisor",
        email: "supervisor@demo.com",
        name: "Security Supervisor",
        role: "SUPERVISOR",
        password: "secureDemo123",
        employeeId: "EMP-SUP"
      },
      "guard@demo.com": {
        id: "demo-guard",
        email: "guard@demo.com",
        name: "Security Guard",
        role: "GUARD",
        password: "secureDemo123",
        employeeId: "EMP-GUARD"
      },
      "guard@emory.local": {
        id: "emory-guard",
        email: "guard@emory.local",
        name: "Emory Guard",
        role: "GUARD",
        password: "secureDemo123",
        employeeId: "EMP-EMORY"
      },
      // Supervisors - one per hospital (14 total)
      // EUH Main Site
      "supervisor.erh@emory.edu": {
        id: "sup-erh",
        email: "supervisor.erh@emory.edu",
        name: "ERH Supervisor",
        role: "SUPERVISOR",
        hospital_id: "d759f721-003c-4401-8187-470f27e36508",
        password: "secureDemo123"
      },
      "supervisor.euh@emory.edu": {
        id: "sup-euh",
        email: "supervisor.euh@emory.edu",
        name: "EUH Main Supervisor",
        role: "SUPERVISOR",
        hospital_id: "b1e35c34-eed1-40ff-a34f-7701f6c07fa3",
        password: "secureDemo123"
      },
      "supervisor.euh.clifton@emory.edu": {
        id: "sup-euh-clifton",
        email: "supervisor.euh.clifton@emory.edu",
        name: "EUH Clifton Tower Supervisor",
        role: "SUPERVISOR",
        hospital_id: "aabbccdd-eeff-0011-2233-445566778899",
        password: "secureDemo123"
      },
      // EUOSH Main Site
      "supervisor.euosh@emory.edu": {
        id: "sup-euosh",
        email: "supervisor.euosh@emory.edu",
        name: "EUOSH Supervisor",
        role: "SUPERVISOR",
        hospital_id: "bbccddee-ff00-1122-3344-556677889900",
        password: "secureDemo123"
      },
      // TEC Main Site
      "supervisor.tec@emory.edu": {
        id: "sup-tec",
        email: "supervisor.tec@emory.edu",
        name: "TEC Clinics Supervisor",
        role: "SUPERVISOR",
        hospital_id: "ccddee00-1122-3344-5566-778899aabbcc",
        password: "secureDemo123"
      },
      "supervisor.emsi@emory.edu": {
        id: "sup-emsi",
        email: "supervisor.emsi@emory.edu",
        name: "EMSI Supervisor",
        role: "SUPERVISOR",
        hospital_id: "d3b07384-d992-4473-82a5-4d152d4372af",
        password: "secureDemo123"
      },
      "supervisor.northlake@emory.edu": {
        id: "sup-northlake",
        email: "supervisor.northlake@emory.edu",
        name: "Northlake Supervisor",
        role: "SUPERVISOR",
        hospital_id: "61899120-4a8b-497d-862d-94436577382d",
        password: "secureDemo123"
      },
      "supervisor.execpark@emory.edu": {
        id: "sup-execpark",
        email: "supervisor.execpark@emory.edu",
        name: "Executive Park 12 Supervisor",
        role: "SUPERVISOR",
        hospital_id: "ddeeff00-1122-3344-5566-778899aabbcc",
        password: "secureDemo123"
      },
      // EDH Main Site
      "supervisor.edh@emory.edu": {
        id: "sup-edh",
        email: "supervisor.edh@emory.edu",
        name: "EDH Supervisor",
        role: "SUPERVISOR",
        hospital_id: "264a4888-18b2-44b4-9ef6-a4c7fc8be910",
        password: "secureDemo123"
      },
      // ELTAC Main Site
      "supervisor.eltac@emory.edu": {
        id: "sup-eltac",
        email: "supervisor.eltac@emory.edu",
        name: "ELTAC Supervisor",
        role: "SUPERVISOR",
        hospital_id: "f2dbe8a4-2844-4a6c-8a45-d5b1bf76832d",
        password: "secureDemo123"
      },
      // EHH Main Site
      "supervisor.ehh@emory.edu": {
        id: "sup-ehh",
        email: "supervisor.ehh@emory.edu",
        name: "EHH Supervisor",
        role: "SUPERVISOR",
        hospital_id: "ffa8dbb7-d0c1-402d-af01-a313f1495bc4",
        password: "secureDemo123"
      },
      // ESJH Main Site
      "supervisor.esjh@emory.edu": {
        id: "sup-esjh",
        email: "supervisor.esjh@emory.edu",
        name: "ESJH Supervisor",
        role: "SUPERVISOR",
        hospital_id: "2a664de3-f450-4415-bb2f-9ae230b7f117",
        password: "secureDemo123"
      },
      // EJCH Main Site
      "supervisor.ejch@emory.edu": {
        id: "sup-ejch",
        email: "supervisor.ejch@emory.edu",
        name: "EJCH Supervisor",
        role: "SUPERVISOR",
        hospital_id: "85629923-42f4-4ee9-889d-56a55dc041f6",
        password: "secureDemo123"
      },
      // EUHM Main Site
      "supervisor.euhm@emory.edu": {
        id: "sup-euhm",
        email: "supervisor.euhm@emory.edu",
        name: "EUHM Supervisor",
        role: "SUPERVISOR",
        hospital_id: "337be113-11d5-4933-b5e7-516994f4cfa2",
        password: "secureDemo123"
      },
      // Directors
      "director.sarge@emory.edu": {
        id: "dir-sarge-euh",
        email: "director.sarge@emory.edu",
        name: "Sarge",
        role: "DIRECTOR",
        main_site_id: "1a1a1a1a-1111-1111-1111-111111111111",
        password: "secureDemo123"
      },
      "director.sarge.euosh@emory.edu": {
        id: "dir-sarge-euosh",
        email: "director.sarge.euosh@emory.edu",
        name: "Sarge (EUOSH)",
        role: "DIRECTOR",
        main_site_id: "2b2b2b2b-2222-2222-2222-222222222222",
        password: "secureDemo123"
      },
      "director.gibson@emory.edu": {
        id: "dir-gibson",
        email: "director.gibson@emory.edu",
        name: "Gibson",
        role: "DIRECTOR",
        main_site_id: "7491bb8a-2374-4e14-a76a-3ef363f52d94",
        password: "secureDemo123"
      },
      "director.randall.edh@emory.edu": {
        id: "dir-randall-edh",
        email: "director.randall.edh@emory.edu",
        name: "Randall (EDH)",
        role: "DIRECTOR",
        main_site_id: "4d4d4d4d-4444-4444-4444-444444444444",
        password: "secureDemo123"
      },
      "director.randall.eltac@emory.edu": {
        id: "dir-randall-eltac",
        email: "director.randall.eltac@emory.edu",
        name: "Randall (ELTAC)",
        role: "DIRECTOR",
        main_site_id: "5e5e5e5e-5555-5555-5555-555555555555",
        password: "secureDemo123"
      },
      "director.greene@emory.edu": {
        id: "dir-greene",
        email: "director.greene@emory.edu",
        name: "Greene",
        role: "DIRECTOR",
        main_site_id: "6f6f6f6f-6666-6666-6666-666666666666",
        password: "secureDemo123"
      },
      "director.refour@emory.edu": {
        id: "dir-refour",
        email: "director.refour@emory.edu",
        name: "Refour",
        role: "DIRECTOR",
        main_site_id: "7a7a7a7a-7777-7777-7777-777777777777",
        password: "secureDemo123"
      },
      "director.panos@emory.edu": {
        id: "dir-panos",
        email: "director.panos@emory.edu",
        name: "Panos",
        role: "DIRECTOR",
        main_site_id: "8b8b8b8b-8888-8888-8888-888888888888",
        password: "secureDemo123"
      },
      "director.jackson@emory.edu": {
        id: "dir-jackson",
        email: "director.jackson@emory.edu",
        name: "Jackson",
        role: "DIRECTOR",
        main_site_id: "9c9c9c9c-9999-9999-9999-999999999999",
        password: "secureDemo123"
      }
    };

    // Hardcoded demo accounts ship in the public JS bundle, so they are only
    // honoured in builds that opt in (VITE_ENABLE_DEMO_LOGIN=true). They never
    // had a real server session anyway; with the X-User-Email fallback removed
    // they could not load any data.
    const normalizedEmail = email.trim().toLowerCase();
    const demoUser = import.meta.env.VITE_ENABLE_DEMO_LOGIN === "true" ? demoUsers[normalizedEmail] : undefined;

    if (demoUser && demoUser.password === password) {
      // Demo login successful
      const deviceId = localStorage.getItem("deviceId") || "demo-device-" + Date.now();
      localStorage.setItem("deviceId", deviceId);
      localStorage.setItem("csrfToken", "demo-token");
      localStorage.setItem("demoMode", "true");

      const user: AuthUser = {
        id: demoUser.id,
        email: demoUser.email,
        name: demoUser.name,
        role: demoUser.role,
        hospital_id: (demoUser as any).hospital_id ?? null,
        hospital_ids: (demoUser as any).hospital_id ? [(demoUser as any).hospital_id] : null,
        main_site_id: (demoUser as any).main_site_id ?? null,
        employeeId: demoUser.employeeId ?? null
      };

      localStorage.setItem("authUser", JSON.stringify(user));
      set({ user, deviceId, token: null, loading: false, initialized: true });
      return;
    }

    // Check for approved applications in localStorage
    const storedApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");
    // Browser-local "approved applications" have no server session either, so
    // they are demo-only too.
    const approvedUser = import.meta.env.VITE_ENABLE_DEMO_LOGIN === "true"
      ? storedApplications.find((app: any) =>
          app.email === email && app.password === password && app.status === 'APPROVED'
        )
      : undefined;

    if (approvedUser) {
      // Approved user login successful
      const deviceId = localStorage.getItem("deviceId") || "approved-device-" + Date.now();
      localStorage.setItem("deviceId", deviceId);
      localStorage.setItem("csrfToken", "approved-token");
      localStorage.setItem("demoMode", "true");

      const user: AuthUser = {
        id: approvedUser.id,
        email: approvedUser.email,
        name: `${approvedUser.firstName} ${approvedUser.lastName}`,
        role: approvedUser.requestedRole,
        status: approvedUser.status,
        employeeId: approvedUser.employeeId ?? null
      };

      localStorage.setItem("authUser", JSON.stringify(user));
      set({ user, deviceId, token: null, loading: false, initialized: true });
      return;
    }

    // Check for pending applications
    const pendingUser = storedApplications.find((app: any) =>
      app.email === email && app.password === password && app.status === 'PENDING'
    );

    if (pendingUser) {
      set({ loading: false });
      throw new Error("Your application is still under review. Please wait for approval before signing in.");
    }

    // Check for rejected applications
    const rejectedUser = storedApplications.find((app: any) =>
      app.email === email && app.password === password && app.status === 'REJECTED'
    );

    if (rejectedUser) {
      set({ loading: false });
      throw new Error("Your application was not approved. Please contact administration for more information.");
    }

    try {
      const guardResult = await loginGuardViaApi(email, password);
      if (guardResult) {
        const deviceId = localStorage.getItem("deviceId") || "guard-device-" + Date.now();
        localStorage.setItem("deviceId", deviceId);
        localStorage.setItem("csrfToken", "guard-token");
        localStorage.setItem("demoMode", "true");
        if (guardResult.token) {
          localStorage.setItem("authToken", guardResult.token);
        }
        const user: AuthUser = {
          id: guardResult.guard.id,
          email: guardResult.guard.email,
          name: guardResult.guard.name,
          role: (guardResult.guard.role as UserRole) ?? "GUARD",
          hospital_id: guardResult.guard.hospital_id ?? null,
          hospital_ids:
            guardResult.hospital_ids ??
            guardResult.guard.hospital_ids ??
            (guardResult.guard.hospital_id ? [guardResult.guard.hospital_id] : null),
          employeeId: guardResult.guard.employee_id ?? null
        };
        localStorage.setItem("authUser", JSON.stringify(user));
        set({ user, deviceId, token: guardResult.token ?? null, loading: false, initialized: true });
        scheduleSessionExpiry(guardResult.token ?? null);
        return;
      }
    } catch (err) {
      if ((err as { rateLimited?: boolean } | null)?.rateLimited === true) {
        set({ loading: false, initialized: true });
        throw err;
      }
      // Guard API login failed, will try backend login
    }

    // Try real backend login
    try {
      const response: any = await apiClient.post("auth/login", {
        json: {
          email,
          password,
          deviceId: localStorage.getItem("deviceId"),
          userAgent: navigator.userAgent
        }
      }).json();

      localStorage.setItem("deviceId", response.deviceId);
      localStorage.setItem("csrfToken", response.csrfToken ?? "");
      localStorage.removeItem("demoMode");
      if (response.token) {
        localStorage.setItem("authToken", response.token);
      }
      localStorage.setItem("authUser", JSON.stringify(response.user));
      set({ user: response.user, deviceId: response.deviceId, token: response.token ?? null, loading: false, initialized: true });
      scheduleSessionExpiry(response.token ?? null);
    } catch (error) {
      set({ loading: false, initialized: true });

      // If backend is down and not using demo credentials, throw a helpful error
      if (error instanceof Error && (error.message.includes("ECONNREFUSED") || error.message.includes("405"))) {
        throw new Error("Invalid email or password. Please check your credentials and try again.");
      }
      throw new Error("Invalid email or password. Please check your credentials and try again.");
    }
  },
  async refresh() {
    set({ loading: true });

    // Check if in demo mode — restore user from localStorage
    // (real guard/supervisor logins also set demoMode). Only restore when the
    // stored session token is still valid; otherwise the user must sign in.
    if (localStorage.getItem("demoMode") === "true") {
      try {
        const stored = localStorage.getItem("authUser");
        const token = localStorage.getItem("authToken");
        const demoAllowed = import.meta.env.VITE_ENABLE_DEMO_LOGIN === "true" && !token;
        if (stored && (tokenIsValid(token) || demoAllowed)) {
          const user = JSON.parse(stored) as AuthUser;
          set({ user, token, loading: false, initialized: true });
          scheduleSessionExpiry(token);
          return;
        }
        if (stored) {
          // Expired or missing token — clear the stale session.
          localStorage.removeItem("authToken");
          localStorage.removeItem("authUser");
          localStorage.removeItem("demoMode");
          set({ user: null, token: null, loading: false, initialized: true });
          return;
        }
      } catch {
        // Corrupted data — fall through to clear session
      }
      // No stored user but demoMode flag set — stale session, clear it
      localStorage.removeItem("demoMode");
      localStorage.removeItem("authUser");
      set({ user: null, loading: false, initialized: true });
      return;
    }

    // Non-demo: try real API refresh
    try {
      const response: any = await apiClient.post("auth/refresh", {
        json: {
          deviceId: localStorage.getItem("deviceId") ?? undefined
        }
      }).json();
      if (response.csrfToken) {
        localStorage.setItem("csrfToken", response.csrfToken);
      }
      const me: any = await apiClient.get("me").json();
      localStorage.setItem("authUser", JSON.stringify(me.user));
      set({ user: me.user, loading: false, initialized: true });
    } catch {
      // API unavailable or session expired — check for stored user as fallback
      localStorage.removeItem("authUser");
      set({ user: null, loading: false, initialized: true });
    }
  },
  async logout() {
    scheduleSessionExpiry(null);
    if (localStorage.getItem("demoMode") !== "true") {
      await apiClient.post("auth/logout").catch(() => undefined);
    }
    localStorage.removeItem("csrfToken");
    localStorage.removeItem("demoMode");
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    set({ user: null, token: null, initialized: true });
  },
  async signup(formData) {
    set({ loading: true });

    try {
      // In demo mode, just simulate successful submission
      if (localStorage.getItem("demoMode") === "true") {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Store pending application in localStorage for demo
        const existingApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");
        const newApplication = {
          id: Date.now().toString(),
          ...formData,
          submittedAt: new Date().toISOString(),
          status: 'PENDING'
        };
        existingApplications.push(newApplication);
        localStorage.setItem("pendingApplications", JSON.stringify(existingApplications));

        set({ loading: false });
        return;
      }

      // Real API call for signup
      await apiClient.post("auth/signup", {
        json: formData
      }).json();

      set({ loading: false });
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  }
}));

class RateLimitedError extends Error {
  readonly rateLimited = true;
}

async function loginGuardViaApi(email: string, password: string) {
  const response = await fetch("/api/guards/login", {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify({ email, password })
  });

  if (response.status === 401) {
    return null;
  }

  // Too many failed attempts: surface the server's own message ("please wait N
  // minutes") instead of letting the caller fall back to "Invalid email or password".
  if (response.status === 429) {
    let message = "Too many failed sign-in attempts. Please wait a few minutes and try again.";
    try {
      const data = await response.json();
      if (data && typeof data.error === "string" && data.error) message = data.error;
    } catch {
      // keep the default message
    }
    throw new RateLimitedError(message);
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Guard login failed (${response.status})`);
  }

  const data = (await response.json()) as {
    guard: {
      id: string;
      email: string;
      name: string;
      role: string;
      hospital_id?: string | null;
      hospital_ids?: string[] | null;
      employee_id?: string | null;
    };
    token?: string;
    hospital_ids?: string[] | null;
  };
  return { guard: data.guard, token: data.token, hospital_ids: data.hospital_ids ?? null };
}
